import { useMemo, useState } from "react";
import {
  BinocularsIcon,
  BedIcon,
  DropIcon,
  FlagBannerIcon,
  ForkKnifeIcon,
  MapPinIcon,
  PlusIcon,
  ShoppingCartIcon,
  TentIcon,
  WarningIcon,
  WrenchIcon,
} from "@phosphor-icons/react";
import MapView from "./MapView";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

const POI_TYPE_META = {
  hazard: { label: "Hazard", iconName: "Warning", Icon: WarningIcon },
  water_source: { label: "Water Source", iconName: "Drop", Icon: DropIcon },
  restaurant: {
    label: "Restaurant",
    iconName: "ForkKnife",
    Icon: ForkKnifeIcon,
  },
  grocery_store: {
    label: "Grocery Store",
    iconName: "ShoppingCart",
    Icon: ShoppingCartIcon,
  },
  bike_repair: { label: "Bike Repair", iconName: "Wrench", Icon: WrenchIcon },
  transportation: {
    label: "Transportation",
    iconName: "MapPin",
    Icon: MapPinIcon,
  },
  scenic_viewpoint: {
    label: "Scenic Viewpoint",
    iconName: "Binoculars",
    Icon: BinocularsIcon,
  },
  historical_marker: {
    label: "Historical Marker",
    iconName: "FlagBanner",
    Icon: FlagBannerIcon,
  },
  camping: {
    label: "Camping",
    iconName: "Tent",
    Icon: TentIcon,
  },
  lodging: {
    label: "Lodging",
    iconName: "Bed",
    Icon: BedIcon,
  },
  other: { label: "Other", iconName: "MapPin", Icon: MapPinIcon },
};

const POI_TYPES = Object.keys(POI_TYPE_META);
const POI_TYPE_HELP = POI_TYPES.join(" | ");

const getPoiTypeMeta = (type) => POI_TYPE_META[type] || POI_TYPE_META.other;

function createDefaultPOI(gpxPoints = []) {
  const first =
    Array.isArray(gpxPoints) && gpxPoints.length > 0 ? gpxPoints[0] : null;
  const otherMeta = getPoiTypeMeta("other");
  return {
    id: `poi_${Date.now()}_${Math.floor(Math.random() * 100000)}`,
    title: "New POI",
    description: "",
    url: "",
    images: [],
    lat: Array.isArray(first) ? Number(first[0]) || 0 : 0,
    lng: Array.isArray(first) ? Number(first[1]) || 0 : 0,
    type: "other",
    icon: otherMeta.iconName,
    hidden: false,
  };
}

export default function PoiEditor({
  pois = [],
  onChange,
  gpxPoints = [],
  secondaryGpxPoints = [],
  className = "",
  title = "Points of Interest",
}) {
  const [editingIndex, setEditingIndex] = useState(null);

  const visiblePois = useMemo(
    () => (pois || []).filter((p) => !p?.hidden),
    [pois],
  );

  const setPoi = (idx, patch) => {
    const next = [...(pois || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removePoi = (idx) => {
    const next = [...(pois || [])];
    next.splice(idx, 1);
    onChange(next);
    if (editingIndex === idx) {
      setEditingIndex(null);
    } else if (editingIndex > idx) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const addPoi = () => {
    onChange([...(pois || []), createDefaultPOI(gpxPoints)]);
    setEditingIndex((pois || []).length);
  };

  const onMapClick = ({ lat, lng }) => {
    if (editingIndex == null || !pois[editingIndex]) return;
    setPoi(editingIndex, {
      lat: Number(lat.toFixed(6)),
      lng: Number(lng.toFixed(6)),
      hidden: false,
    });
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <Label>{title}</Label>
        <Button type="button" size="sm" onClick={addPoi}>
          <PlusIcon size={14} className="mr-1" /> Add POI
        </Button>
      </div>

      <div className="mt-2 h-72 rounded-xl overflow-hidden border border-[#E5E0D8] bg-white">
        {(gpxPoints || []).length > 1 || visiblePois.length > 0 ? (
          <MapView
            gpxPoints={gpxPoints || []}
            secondaryGpxPoints={secondaryGpxPoints || []}
            pois={visiblePois}
            activePoiId={
              editingIndex != null && pois[editingIndex]
                ? pois[editingIndex].id
                : null
            }
            onMapClick={onMapClick}
            height="100%"
            fit
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-sm text-[#5C665F] px-4 text-center">
            Add POIs or upload GPX points to preview them on the map.
          </div>
        )}
      </div>

      <div className="mt-2 text-xs text-gray-600">
        type: {POI_TYPE_HELP}
        {editingIndex != null ? " · click map to set lat/lng for selected POI" : ""}
      </div>

      <div className="mt-3 space-y-2" data-testid="poi-editor-list">
        {(pois || []).map((p, idx) => {
          const isEditing = editingIndex === idx;
          const isHidden = !!p?.hidden;
          const typeMeta = getPoiTypeMeta(p?.type);
          const TypeIcon = typeMeta.Icon;
          return (
            <div
              key={p?.id || `poi-${idx}`}
              className={`border rounded-lg p-3 ${isHidden ? "opacity-60 bg-[#F7F7F7]" : "bg-white"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {p?.title || "Untitled POI"}
                  </div>
                  <div className="text-xs text-[#5C665F]">
                    <span className="inline-flex items-center gap-1">
                      <TypeIcon size={12} weight="fill" />
                      {typeMeta.label}
                    </span>
                    {" · "}
                    {Number(p?.lat) || 0}, {Number(p?.lng) || 0}
                    {isHidden ? " · hidden" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingIndex(isEditing ? null : idx)}
                  >
                    {isEditing ? "Done" : "Edit"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPoi(idx, { hidden: !isHidden })}
                  >
                    {isHidden ? "Show" : "Hide"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePoi(idx)}
                  >
                    Remove
                  </Button>
                </div>
              </div>

              {isEditing && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={p?.title || ""}
                      onChange={(e) => setPoi(idx, { title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select
                      value={POI_TYPES.includes(p?.type) ? p.type : "other"}
                      onValueChange={(val) => {
                        const meta = getPoiTypeMeta(val);
                        setPoi(idx, { type: val, icon: meta.iconName });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {POI_TYPES.map((type) => {
                          const meta = getPoiTypeMeta(type);
                          const MetaIcon = meta.Icon;
                          return (
                            <SelectItem key={type} value={type}>
                              <span className="inline-flex items-center gap-2">
                                <MetaIcon size={14} weight="fill" />
                                {meta.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Location</Label>
                    <div className="mt-1 flex items-center gap-3 rounded-md border border-input bg-muted px-3 py-2 text-sm">
                      <MapPinIcon size={14} className="shrink-0 text-[#5C665F]" />
                      {p?.lat || p?.lng ? (
                        <span className="font-mono">
                          {Number(p.lat).toFixed(6)}, {Number(p.lng).toFixed(6)}
                        </span>
                      ) : (
                        <span className="text-[#5C665F]">No location set</span>
                      )}
                      <span className="ml-auto text-xs text-[#5C665F]">
                        Click the map above to place
                      </span>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      rows={2}
                      value={p?.description || ""}
                      onChange={(e) =>
                        setPoi(idx, { description: e.target.value })
                      }
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Website URL</Label>
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={p?.url || ""}
                      onChange={(e) => setPoi(idx, { url: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <Label>Images</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setPoi(idx, { images: [...(p?.images || []), ""] })
                        }
                      >
                        <PlusIcon size={12} className="mr-1" /> Add Image
                      </Button>
                    </div>
                    {(p?.images || []).length > 0 && (
                      <div className="mt-1 space-y-1">
                        {(p.images || []).map((imgUrl, imgIdx) => (
                          <div key={imgIdx} className="flex items-center gap-2">
                            <Input
                              type="url"
                              placeholder="https://..."
                              value={imgUrl}
                              onChange={(e) => {
                                const next = [...(p.images || [])];
                                next[imgIdx] = e.target.value;
                                setPoi(idx, { images: next });
                              }}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const next = [...(p.images || [])];
                                next.splice(imgIdx, 1);
                                setPoi(idx, { images: next });
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {(pois || []).length === 0 && (
          <div className="text-sm text-[#5C665F]">No POIs added yet.</div>
        )}
      </div>
    </div>
  );
}